import { useCallback, useMemo, useState } from "react"

import { useLocation } from "react-router-dom"

import type { CanvasPaneProps } from "@/features/canvas/components/canvas-pane"

import { CanvasPane } from "@/features/canvas/components/canvas-pane"
import { useCanvasStore } from "@/features/canvas/stores/canvas-store"
import { DocumentShareDialog } from "@/features/documents/components/document-share-dialog"
import { useDocument } from "@/hooks/use-documents"
import { useViewportBelow } from "@/hooks/use-viewport-below"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"

const COMPACT_LAYOUT_BREAKPOINT = 1024

type CanvasSidebarPanelProps = {
  paneProps?: Partial<CanvasPaneProps>
}

export function CanvasSidebarPanel({ paneProps }: CanvasSidebarPanelProps) {
  const location = useLocation()
  const documentId = useCanvasStore((state) => state.documentId)
  const { data: userDocument } = useDocument(documentId ?? undefined)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const isCompactLayout = useViewportBelow({ breakpoint: COMPACT_LAYOUT_BREAKPOINT })

  const isDocumentRoute = location.pathname.startsWith("/documents/")
  const isCanvasFocused = useContentSidebarStore(
    (state) => state.currentPanel?.id === "canvas" && state.isFocused
  )

  const handleReturnToChat = useCallback(() => {
    useContentSidebarStore.setState({ isFocused: false })
  }, [])

  const resolvedPaneProps = useMemo(() => {
    if (!paneProps) {
      return undefined
    }

    if (isDocumentRoute) {
      return paneProps
    }

    const {
      onBackToDocuments,
      backButtonLabel,
      onStartNewChatWithDocument,
      startChatButtonLabel,
      canDeleteDocument,
      ...restPaneProps
    } = paneProps

    return restPaneProps
  }, [isDocumentRoute, paneProps])

  const hasExplicitShareHandler = typeof resolvedPaneProps?.onShareDocument === "function"
  const canShareDocument =
    resolvedPaneProps?.canShareDocument ?? userDocument?.currentUserRole === "owner"
  const onShareDocument =
    resolvedPaneProps?.onShareDocument ??
    (canShareDocument ? () => setShareDialogOpen(true) : undefined)

  return (
    <>
      <CanvasPane
        {...resolvedPaneProps}
        canShareDocument={canShareDocument}
        isActiveView={!isCompactLayout || isCanvasFocused}
        isPhone={isCompactLayout}
        onShareDocument={onShareDocument}
        onReturnToChat={
          isCompactLayout && !isDocumentRoute
            ? handleReturnToChat
            : resolvedPaneProps?.onReturnToChat
        }
      />
      {!hasExplicitShareHandler && documentId && userDocument && (
        <DocumentShareDialog
          currentUserRole={userDocument.currentUserRole}
          documentId={documentId}
          open={shareDialogOpen}
          ownerUserId={userDocument.ownerUserId}
          onOpenChange={setShareDialogOpen}
        />
      )}
    </>
  )
}
